import { ApiResponse } from 'apisauce'
import { useState } from 'react'
import snackbarStore from 'src/stores/snackbar'
import {
  ajustarTabelaFrete,
  createNumeroPC,
  criarTabelaFrete,
  vincularTabelaCliente
} from './lotesRepository'

export type AcaoTabelaFrete =
  | 'criarTabelaFrete'
  | 'criarPC'
  | 'criarTabelaCliente'
  | 'reajuste'

interface OpcoesAcao<T> {
  chamada: () => Promise<ApiResponse<T, ApiErrorResponse>>
  mensagemSucesso: (data: T | undefined) => string
  mensagemErroPadrao: string
  // Retorna a mensagem de erro quando uma resposta 2xx ainda representa falha (ex.: PC do Rodopar)
  validarResposta?: (data: T | undefined) => string | null
}

// Erro 4xx de integração costuma vir de dado do lote (campo sem mapeamento, IBGE ausente): tentar de novo não resolve.
const DICA_DADO_INVALIDO = ' Corrija o dado indicado antes de tentar novamente.'

export function useAcoesTabelaFrete(loteId: string, onChanged: () => void) {
  const [executando, setExecutando] = useState<AcaoTabelaFrete | null>(null)

  async function executar<T>(acao: AcaoTabelaFrete, opcoes: OpcoesAcao<T>) {
    setExecutando(acao)

    try {
      const response = await opcoes.chamada()

      if (!response.ok) {
        const mensagem = response.data?.message ?? opcoes.mensagemErroPadrao
        snackbarStore.setMessage(
          response.status === 400 ? mensagem + DICA_DADO_INVALIDO : mensagem
        )
        return
      }

      const erro = opcoes.validarResposta?.(response.data) ?? null
      if (erro) {
        snackbarStore.setMessage(erro)
        return
      }

      snackbarStore.setMessage(opcoes.mensagemSucesso(response.data))
    } finally {
      setExecutando(null)
      // Mesmo numa resposta negativa o lote pode ter mudado (ex.: RODFRA grava o CODFRE
      // mesmo quando o itinerário falha) — recarrega sempre, não só no sucesso.
      onChanged()
    }
  }

  return {
    executando,
    criarTabelaFrete: () =>
      executar('criarTabelaFrete', {
        chamada: () => criarTabelaFrete(loteId),
        mensagemSucesso: () => 'Tabela de frete criada com sucesso!',
        mensagemErroPadrao: 'Não foi possível criar a tabela de frete.'
      }),
    criarPC: () =>
      executar('criarPC', {
        chamada: () => createNumeroPC(loteId),
        mensagemSucesso: () => 'PC criada com sucesso!',
        mensagemErroPadrao: 'Erro ao criar PC',
        validarResposta: data => {
          // O create-pc devolve a resposta crua do Rodopar, que pode vir como array ou objeto.
          const resposta = Array.isArray(data) ? data[0] : data

          return resposta?.SUCESSO
            ? null
            : resposta?.LOG_INTEGRACAO ??
                'Ops... algum erro ocorreu na hora de integrar com Rodopar'
        }
      }),
    criarTabelaCliente: () =>
      executar('criarTabelaCliente', {
        chamada: () => vincularTabelaCliente(loteId),
        mensagemSucesso: data =>
          data?.jaIntegrado
            ? 'A tabela por cliente já estava integrada.'
            : 'Tabela por cliente criada com sucesso!',
        mensagemErroPadrao: 'Não foi possível criar a tabela por cliente.'
      }),
    reajustar: () =>
      executar('reajuste', {
        chamada: () => ajustarTabelaFrete(loteId),
        mensagemSucesso: () => 'Reajuste de valores enviado com sucesso!',
        mensagemErroPadrao: 'Não foi possível enviar o reajuste de valores.'
      })
  }
}
